import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
    Table, Button, Space, Switch, Popconfirm, message, Image, Typography,
} from 'antd';
import { PlusOutlined, ArrowUpOutlined, ArrowDownOutlined } from '@ant-design/icons';
import { useQuery, useMutation, useApolloClient } from '@apollo/client';
import {
    LIST_COMMON_CONFIGS_BY_CONFIG_KEY,
    CREATE_COMMON_CONFIG,
    UPDATE_COMMON_CONFIG,
    BATCH_DELETE_COMMON_CONFIGS,
    BATCH_UPDATE_COMMON_CONFIG_STATUS,
} from './graphql/queries';
import type {
    CommonConfigType, ListCommonConfigsByConfigKeyResponse,
} from './graphql/queries';
import AppActivityDrawer from './components/AppActivityDrawer';
import {
    parseActivityContent, generateActivitySubKey,
} from './utils/activityConfig';
import type { SerializedRule } from './utils/activityConfig';

const MASTER_KEY = 'ALL_APP_ACTIVITIES';
// 与后端上限一致：最多 10 个活动、每个活动最多 10 条规则
const MAX_ACTIVITIES = 10;
const MAX_RULES_PER_ACTIVITY = 10;

interface ActivityRow {
    subKey: string;
    // 同一活动下的多行配置（内容相同，灰度规则不同）；null 表示尚未加载
    configs: CommonConfigType[] | null;
    index: number;
}

const AppActivityPage: React.FC = () => {
    const apolloClient = useApolloClient();
    const [drawerVisible, setDrawerVisible] = useState(false);
    const [editingRow, setEditingRow] = useState<ActivityRow | null>(null);
    const [submitting, setSubmitting] = useState(false);
    const [subConfigsByKey, setSubConfigsByKey] = useState<Record<string, CommonConfigType[]>>({});

    const { data: masterData, loading: masterLoading, refetch: refetchMaster } = useQuery<
        ListCommonConfigsByConfigKeyResponse
    >(LIST_COMMON_CONFIGS_BY_CONFIG_KEY, {
        variables: { configKey: MASTER_KEY },
        fetchPolicy: 'network-only',
    });

    const masterConfig: CommonConfigType | null = masterData?.listCommonConfigsByConfigKey?.[0] ?? null;

    const subKeys: string[] = useMemo(() => {
        if (!masterConfig?.configValue) return [];
        try {
            return JSON.parse(masterConfig.configValue) as string[];
        } catch {
            return [];
        }
    }, [masterConfig]);

    const refetchSubKey = useCallback(async (subKey: string) => {
        try {
            const res = await apolloClient.query<ListCommonConfigsByConfigKeyResponse>({
                query: LIST_COMMON_CONFIGS_BY_CONFIG_KEY,
                variables: { configKey: subKey },
                fetchPolicy: 'network-only',
            });
            const rows = res.data?.listCommonConfigsByConfigKey ?? [];
            setSubConfigsByKey((prev) => ({ ...prev, [subKey]: rows }));
        } catch {
            setSubConfigsByKey((prev) => ({ ...prev, [subKey]: [] }));
        }
    }, [apolloClient]);

    useEffect(() => {
        const missing = subKeys.filter((k) => !(k in subConfigsByKey));
        if (missing.length === 0) return;
        missing.forEach((key) => {
            refetchSubKey(key);
        });
    }, [subKeys, subConfigsByKey, refetchSubKey]);

    const rows: ActivityRow[] = useMemo(
        () => subKeys.map((subKey, index) => ({
            subKey,
            configs: subConfigsByKey[subKey] ?? null,
            index,
        })),
        [subKeys, subConfigsByKey],
    );

    const [createCommonConfig] = useMutation(CREATE_COMMON_CONFIG);
    const [updateCommonConfig] = useMutation(UPDATE_COMMON_CONFIG);
    const [batchDeleteCommonConfigs] = useMutation(BATCH_DELETE_COMMON_CONFIGS);
    const [batchUpdateCommonConfigStatus] = useMutation(BATCH_UPDATE_COMMON_CONFIG_STATUS);

    // 删除需先置 inactive（后端 deleteConfig 拒绝删除 active 行）：先批量置灰再批量删
    const deactivateAndDelete = useCallback(
        async (ids: string[]) => {
            if (ids.length === 0) return;
            await batchUpdateCommonConfigStatus({ variables: { ids, isActive: false } });
            await batchDeleteCommonConfigs({ variables: { ids } });
        },
        [batchUpdateCommonConfigStatus, batchDeleteCommonConfigs],
    );

    const updateMasterConfigValue = useCallback(
        async (newSubKeys: string[]) => {
            const configValue = JSON.stringify(newSubKeys);
            if (masterConfig) {
                await updateCommonConfig({
                    variables: { id: masterConfig.id, input: { configValue } },
                });
            } else {
                await createCommonConfig({
                    variables: {
                        input: {
                            configKey: MASTER_KEY,
                            configValue,
                            configType: 'JSON',
                            isActive: true,
                            sortOrder: 0,
                            matchRule: '',
                        },
                    },
                });
            }
        },
        [masterConfig, updateCommonConfig, createCommonConfig],
    );

    const handleAdd = useCallback(() => {
        setEditingRow(null);
        setDrawerVisible(true);
    }, []);

    const handleEdit = useCallback((row: ActivityRow) => {
        setEditingRow(row);
        setDrawerVisible(true);
    }, []);

    const handleDrawerClose = useCallback(() => {
        setDrawerVisible(false);
        setEditingRow(null);
    }, []);

    const handleSubmit = useCallback(
        async (values: { configValue: string; rules: SerializedRule[] }) => {
            setSubmitting(true);
            try {
                // 前端预检，避免无谓请求与残留孤儿行（后端仍有硬校验）
                if (values.rules.length > MAX_RULES_PER_ACTIVITY) {
                    message.error(`每个活动最多 ${MAX_RULES_PER_ACTIVITY} 条规则`);
                    return;
                }
                if (editingRow && editingRow.configs && editingRow.configs.length > 0) {
                    // 编辑：同 subKey 下对现有多行做增/改/删对账
                    const subKey = editingRow.subKey;
                    const existing = editingRow.configs;
                    const keptIds = new Set<string>();
                    // sortOrder 必须在同 subKey 的 active 行间唯一，新行取现有最大值 +1 递增，避免冲突
                    let maxSort = existing.reduce((m, c) => Math.max(m, c.sortOrder ?? -1), -1);
                    for (const rule of values.rules) {
                        if (rule.id != null && existing.some((c) => c.id === rule.id)) {
                            keptIds.add(rule.id);
                            await updateCommonConfig({
                                variables: {
                                    id: rule.id,
                                    input: { configValue: values.configValue, matchRule: rule.matchRule },
                                },
                            });
                        } else {
                            maxSort += 1;
                            await createCommonConfig({
                                variables: {
                                    input: {
                                        configKey: subKey,
                                        configValue: values.configValue,
                                        matchRule: rule.matchRule,
                                        configType: 'JSON',
                                        isActive: true,
                                        sortOrder: maxSort,
                                    },
                                },
                            });
                        }
                    }
                    // 删除被移除的规则行（批量置灰 + 批量删）
                    const removedIds = existing.filter((c) => !keptIds.has(c.id)).map((c) => c.id);
                    await deactivateAndDelete(removedIds);
                    await refetchSubKey(subKey);
                    message.success('更新成功');
                } else {
                    // 新建活动：先校验活动总数上限，避免建完规则行后挂主配置被拒、残留孤儿行
                    if (subKeys.length >= MAX_ACTIVITIES) {
                        message.error(`最多只能创建 ${MAX_ACTIVITIES} 个活动`);
                        return;
                    }
                    // 生成 subKey，按规则逐行创建（sortOrder 从 0 递增），再挂到主配置
                    const newSubKey = generateActivitySubKey();
                    let sort = 0;
                    for (const rule of values.rules) {
                        await createCommonConfig({
                            variables: {
                                input: {
                                    configKey: newSubKey,
                                    configValue: values.configValue,
                                    matchRule: rule.matchRule,
                                    configType: 'JSON',
                                    isActive: true,
                                    sortOrder: sort,
                                },
                            },
                        });
                        sort += 1;
                    }
                    await updateMasterConfigValue([...subKeys, newSubKey]);
                    await refetchMaster();
                    await refetchSubKey(newSubKey);
                    message.success('创建成功');
                }
                setDrawerVisible(false);
                setEditingRow(null);
            } catch {
                message.error('操作失败，请重试');
            } finally {
                setSubmitting(false);
            }
        },
        [
            editingRow, createCommonConfig, updateCommonConfig, deactivateAndDelete,
            subKeys, updateMasterConfigValue, refetchMaster, refetchSubKey,
        ],
    );

    const handleToggleActive = useCallback(
        async (row: ActivityRow, checked: boolean) => {
            if (!row.configs || row.configs.length === 0) return;
            try {
                for (const c of row.configs) {
                    if (c.isActive !== checked) {
                        await updateCommonConfig({ variables: { id: c.id, input: { isActive: checked } } });
                    }
                }
                await refetchSubKey(row.subKey);
                message.success(checked ? '已启用' : '已禁用');
            } catch {
                message.error('操作失败');
            }
        },
        [updateCommonConfig, refetchSubKey],
    );

    const handleDelete = useCallback(
        async (row: ActivityRow) => {
            try {
                const ids = (row.configs ?? []).map((c) => c.id);
                await deactivateAndDelete(ids);
                const newSubKeys = subKeys.filter((_, i) => i !== row.index);
                await updateMasterConfigValue(newSubKeys);
                setSubConfigsByKey((prev) => {
                    const next = { ...prev };
                    delete next[row.subKey];
                    return next;
                });
                await refetchMaster();
                message.success('删除成功');
            } catch {
                message.error('删除失败');
            }
        },
        [subKeys, updateMasterConfigValue, deactivateAndDelete, refetchMaster],
    );

    const handleMove = useCallback(
        async (row: ActivityRow, direction: 'up' | 'down') => {
            const newKeys = [...subKeys];
            const idx = row.index;
            const swapIdx = direction === 'up' ? idx - 1 : idx + 1;
            if (swapIdx < 0 || swapIdx >= newKeys.length) return;
            [newKeys[idx], newKeys[swapIdx]] = [newKeys[swapIdx], newKeys[idx]];
            try {
                await updateMasterConfigValue(newKeys);
                await refetchMaster();
            } catch {
                message.error('排序失败');
            }
        },
        [subKeys, updateMasterConfigValue, refetchMaster],
    );

    const columns = [
        {
            title: '顺序',
            width: 60,
            render: (_: unknown, row: ActivityRow) => row.index + 1,
        },
        {
            title: '活动名称（中文）',
            render: (_: unknown, row: ActivityRow) => {
                if (!row.configs || row.configs.length === 0) return <span style={{ color: '#bbb' }}>加载中...</span>;
                try {
                    const parsed = parseActivityContent(row.configs[0].configValue);
                    return parsed.nameZh || row.subKey;
                } catch {
                    return row.subKey;
                }
            },
        },
        {
            title: '角标',
            width: 80,
            render: (_: unknown, row: ActivityRow) => {
                if (!row.configs || row.configs.length === 0) return null;
                try {
                    const { badge } = parseActivityContent(row.configs[0].configValue);
                    return badge
                        ? <Image src={badge} width={32} height={32} style={{ objectFit: 'contain' }} />
                        : null;
                } catch {
                    return null;
                }
            },
        },
        {
            title: '灰度规则',
            width: 100,
            render: (_: unknown, row: ActivityRow) => {
                if (!row.configs || row.configs.length === 0) return null;
                return (
                    <Typography.Text style={{ fontSize: 12 }}>
                        {row.configs.length} 条规则
                    </Typography.Text>
                );
            },
        },
        {
            title: '启用',
            width: 70,
            render: (_: unknown, row: ActivityRow) => (
                <Switch
                    size="small"
                    checked={row.configs?.[0]?.isActive ?? false}
                    disabled={!row.configs || row.configs.length === 0}
                    onChange={(checked) => handleToggleActive(row, checked)}
                />
            ),
        },
        {
            title: '操作',
            width: 200,
            render: (_: unknown, row: ActivityRow) => (
                <Space size={4}>
                    <Button size="small" onClick={() => handleEdit(row)}>编辑</Button>
                    <Button
                        size="small"
                        icon={<ArrowUpOutlined />}
                        disabled={row.index === 0}
                        onClick={() => handleMove(row, 'up')}
                    />
                    <Button
                        size="small"
                        icon={<ArrowDownOutlined />}
                        disabled={row.index === subKeys.length - 1}
                        onClick={() => handleMove(row, 'down')}
                    />
                    <Popconfirm
                        title="确认删除该活动配置？"
                        onConfirm={() => handleDelete(row)}
                        okText="删除"
                        okType="danger"
                        cancelText="取消"
                    >
                        <Button size="small" danger>删除</Button>
                    </Popconfirm>
                </Space>
            ),
        },
    ];

    return (
        <div style={{ padding: 24 }}>
            <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: 16, fontWeight: 600 }}>活动配置</span>
                <Button
                    type="primary"
                    icon={<PlusOutlined />}
                    disabled={subKeys.length >= MAX_ACTIVITIES}
                    onClick={handleAdd}
                >
                    {subKeys.length >= MAX_ACTIVITIES ? `最多 ${MAX_ACTIVITIES} 个活动` : '新建活动'}
                </Button>
            </div>
            <Table
                rowKey="subKey"
                dataSource={rows}
                columns={columns}
                loading={masterLoading}
                pagination={false}
                size="small"
            />
            <AppActivityDrawer
                visible={drawerVisible}
                editingConfigs={editingRow?.configs ?? null}
                onClose={handleDrawerClose}
                onSubmit={handleSubmit}
                submitting={submitting}
            />
        </div>
    );
};

export default AppActivityPage;
